import { useCallStore, type CallPhase } from '@/state/callStore';

import { tNow } from '@/i18n/languageStore';

/**
 * The ringtone (T146): a call rings once per browser, in the leader tab
 * (realtime/leader.ts), for as long as it is ringing. The sound is generated
 * with Web Audio — a ringtone for incoming calls and a quieter ringback while
 * calling out — so there is no audio file to license. When the browser will
 * not play sound yet (no click on the page so far), the tab title flashes.
 */
export type ToneKind = 'incoming' | 'outgoing';

export interface Tone {
  start(kind: ToneKind): Promise<void>;
  stop(): void;
}

/** Two short beeps every two seconds (incoming), or one long tone every three (ringback). */
export function createWebAudioTone(): Tone {
  let context: AudioContext | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  const beep = (at: number, length: number, frequency: number, volume: number) => {
    if (!context) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(volume, at + 0.02);
    gain.gain.setValueAtTime(volume, at + length - 0.05);
    gain.gain.linearRampToValueAtTime(0, at + length);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + length);
  };
  return {
    async start(kind) {
      const AudioCtx = globalThis.AudioContext;
      if (!AudioCtx) throw new Error('No Web Audio');
      context ??= new AudioCtx();
      await context.resume();
      if (context.state !== 'running')
        throw new DOMException('Sound is blocked', 'NotAllowedError');
      const ring = () => {
        const now = context?.currentTime ?? 0;
        if (kind === 'incoming') {
          beep(now, 0.4, 880, 0.2);
          beep(now + 0.55, 0.4, 880, 0.2);
        } else {
          beep(now, 1.2, 440, 0.08);
        }
      };
      ring();
      timer = setInterval(ring, kind === 'incoming' ? 2_000 : 3_000);
    },
    stop() {
      clearInterval(timer);
      timer = undefined;
      void context?.close().catch(() => undefined);
      context = null;
    },
  };
}

const RINGING: CallPhase[] = ['incoming-ringing', 'outgoing-ringing'];

export interface RingerOptions {
  isLeader: () => boolean;
  createTone?: () => Tone;
}

/** Rings while the call store says so. Returns `stop` (sign-out). */
export function startRinger({
  isLeader,
  createTone = createWebAudioTone,
}: RingerOptions): () => void {
  let tone: Tone | null = null;
  let flash: ReturnType<typeof setInterval> | undefined;
  let savedTitle: string | null = null;

  const silence = () => {
    tone?.stop();
    tone = null;
    clearInterval(flash);
    flash = undefined;
    if (savedTitle !== null) {
      document.title = savedTitle;
      savedTitle = null;
    }
  };

  const flashTitle = (text: string) => {
    savedTitle = document.title;
    let on = false;
    flash = setInterval(() => {
      on = !on;
      document.title = on ? text : (savedTitle ?? document.title);
    }, 1_000);
  };

  const update = (phase: CallPhase, previous?: CallPhase) => {
    const ringing = RINGING.includes(phase);
    if (!ringing) {
      silence();
      return;
    }
    if (phase === previous || tone || !isLeader()) return;
    const kind: ToneKind = phase === 'incoming-ringing' ? 'incoming' : 'outgoing';
    const current = createTone();
    tone = current;
    current.start(kind).catch(() => {
      if (tone !== current) return;
      const name = useCallStore.getState().counterpart?.name ?? 'Neighbor';
      if (kind === 'incoming') flashTitle(tNow('Incoming call · {name}', { name }));
    });
  };

  update(useCallStore.getState().phase);
  const unsubscribe = useCallStore.subscribe((state, previous) =>
    update(state.phase, previous.phase),
  );
  return () => {
    unsubscribe();
    silence();
  };
}
